import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Lock, 
  ClipboardCheck, 
  Scale, 
  GitCompare, 
  History, 
  AlertTriangle,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { AnimatedSection, staggerContainer } from '@/components/animations';

const lockedFeatures = [
  {
    icon: ClipboardCheck,
    title: 'Checklist técnica EN 81',
    description: 'Verificação estruturada baseada na norma europeia'
  },
  {
    icon: Scale,
    title: 'Avaliação de recomendações',
    description: 'Distinguir o necessário do evitável'
  },
  {
    icon: GitCompare,
    title: 'Comparação de soluções',
    description: 'Análise de propostas de diferentes empresas'
  },
  {
    icon: History,
    title: 'Histórico técnico',
    description: 'Registos e evolução do seu elevador'
  },
  {
    icon: AlertTriangle,
    title: 'Alertas de risco',
    description: 'Conformidade e custos evitáveis'
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.4 }
  }
};

function LockedFeatureCard({ feature }: { feature: typeof lockedFeatures[0] }) {
  const [showMessage, setShowMessage] = useState(false);
  const Icon = feature.icon;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip open={showMessage} onOpenChange={setShowMessage}>
        <TooltipTrigger asChild>
          <motion.div variants={itemVariants}>
            <Card 
              className="relative overflow-hidden cursor-pointer group border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg"
              onClick={() => setShowMessage(true)}
              onMouseEnter={() => setShowMessage(true)}
              onMouseLeave={() => setShowMessage(false)}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className="p-2.5 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-foreground text-sm">
                        {feature.title}
                      </h3>
                      <Lock className="w-3.5 h-3.5 text-muted-foreground/60" />
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2">
                      {feature.description}
                    </p>
                  </div>
                </div>
                
                {/* Locked overlay on hover */}
                <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                  <div className="bg-background/95 backdrop-blur-sm px-3 py-2 rounded-lg shadow-lg border border-border">
                    <p className="text-xs text-foreground font-medium flex items-center gap-1.5">
                      <Lock className="w-3 h-3 text-primary" />
                      Disponível após registo
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px] text-center">
          <p className="text-xs">Crie uma conta gratuita para aceder a este conteúdo</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function MemberValuePreview() {
  return (
    <AnimatedSection className="py-16 md:py-24 bg-muted/30">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
            O que ganha quem cria conta no ElevAdvisor
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Ferramentas técnicas e conteúdos práticos que ajudam a decidir com rigor 
            antes de assumir custos, riscos ou responsabilidades legais.
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-10"
        >
          {lockedFeatures.map((feature, index) => (
            <LockedFeatureCard key={index} feature={feature} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button asChild size="lg" className="gap-2">
            <Link to="/members/auth">
              Criar conta gratuita
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg">
            <Link to="/como-funciona">
              Ver como funciona
            </Link>
          </Button>
        </motion.div>
      </div>
    </AnimatedSection>
  );
}
